import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { sitesKey, sitesQuery } from "@/lib/screen-queries";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, MapPin, Loader2, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { PatrolMap } from "@/components/PatrolMap";
import { LocationPermissionBanner } from "@/components/LocationPermissionBanner";
import { AddressSearch } from "@/components/GeofenceWizard";
import { AssignSiteToContractor } from "@/components/contractors/AssignSiteToContractor";

export const Route = createFileRoute("/_authenticated/sites/")({
  component: SitesPage,
  head: () => ({ meta: [{ title: "Sites — MissionReady" }] }),
});

type Site = {
  id: string; name: string; address: string | null; city: string | null; active: boolean;
  latitude: number | null; longitude: number | null;
};

function SitesPage() {
  const { currentOrg, currentRole } = useAuth();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const canEdit = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");

  // Shared cache entry with the prefetcher, so arriving here after a warm-up
  // renders immediately instead of refetching.
  const qc = useQueryClient();
  const orgId = currentOrg?.id ?? "";
  const { data: sitesData } = useQuery(sitesQuery(orgId));
  const sites = (sitesData ?? []) as Site[];
  const load = async () => {
    if (!orgId) return;
    await qc.invalidateQueries({ queryKey: sitesKey(orgId) });
  };

  const [pendingPick, setPendingPick] = useState<{ lat: number; lng: number; label: string } | null>(null);
  const [quickName, setQuickName] = useState("");
  const [quickRadius, setQuickRadius] = useState(150);
  const [quickBusy, setQuickBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState<{ name: string; siteId: string; address: string; radius: number } | null>(null);

  const mapMarkers = useMemo(() => {
    const base = sites
      .filter((s) => s.latitude != null && s.longitude != null)
      .map((s) => ({
        id: s.id,
        name: s.name,
        kind: "inclusion" as const,
        center_lat: Number(s.latitude),
        center_lng: Number(s.longitude),
        radius_m: s.id === selectedId ? 150 : 60,
      }));
    if (pendingPick) {
      base.push({
        id: "__pending",
        name: quickName.trim() || "New site (unsaved)",
        kind: "inclusion" as const,
        center_lat: pendingPick.lat,
        center_lng: pendingPick.lng,
        radius_m: quickRadius,
      });
    }
    return base;
  }, [sites, selectedId, pendingPick, quickName, quickRadius]);

  const focus = useMemo(
    () => (pendingPick ? { lat: pendingPick.lat, lng: pendingPick.lng, zoom: 17 } : null),
    [pendingPick?.lat, pendingPick?.lng],
  );

  const quickSave = async () => {
    if (!currentOrg || !pendingPick) return;
    const name = quickName.trim() || pendingPick.label.split(",")[0];
    if (!name) return toast.error("Give the site a name");
    setQuickBusy(true);
    const { data: site, error } = await supabase.from("sites").insert({
      organization_id: currentOrg.id,
      name,
      address: pendingPick.label,
      latitude: pendingPick.lat,
      longitude: pendingPick.lng,
    }).select("id").single();
    if (error || !site) { setQuickBusy(false); return toast.error(error?.message ?? "Could not create site"); }
    await supabase.from("geofences").insert({
      organization_id: currentOrg.id,
      site_id: site.id,
      name: `${name} perimeter`,
      kind: "inclusion",
      center_lat: pendingPick.lat,
      center_lng: pendingPick.lng,
      radius_m: Math.round(quickRadius),
      active: true,
    });
    setQuickBusy(false);
    setPendingPick(null);
    setQuickName("");
    setQuickRadius(150);
    setSavedFlash({ name, siteId: site.id, address: pendingPick.label, radius: Math.round(quickRadius) });
    toast.success("Site & perimeter added");
    load();
  };


  const removeSite = async (s: Site) => {
    if (!confirm(`Delete site "${s.name}" and its perimeter? This cannot be undone.`)) return;
    setDeletingId(s.id);
    await supabase.from("geofences").delete().eq("site_id", s.id);
    const { error } = await supabase.from("sites").delete().eq("id", s.id);
    setDeletingId(null);
    if (error) return toast.error(error.message);
    toast.success("Site deleted");
    // Drop it from the cached list right away, then refresh in the background.
    qc.setQueryData<Site[]>(sitesKey(orgId), (arr) => (arr ?? []).filter((x) => x.id !== s.id));
    void load();
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Sites</h1>
          <p className="text-sm text-muted-foreground">Reusable site templates for your operations.</p>
        </div>
        {canEdit && (
          <Button asChild size="sm">
            <Link to="/sites/new"><Plus className="mr-1 h-4 w-4" /> Add site</Link>
          </Button>
        )}
      </div>

      <div className="mt-6">
        <LocationPermissionBanner />
      </div>

      {canEdit && (
        <div className="mb-3 rounded-xl border border-[#C0C0C0]/20 bg-card p-3">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Quick add — search an address or tap the map
          </p>
          <AddressSearch onPick={(lat, lng, label) => { setPendingPick({ lat, lng, label }); setQuickName(label.split(",")[0] ?? ""); setSavedFlash(null); }} />

          {savedFlash && !pendingPick && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">Site saved</div>
                <div className="text-muted-foreground">
                  {savedFlash.name} — {savedFlash.address} (perimeter {savedFlash.radius} m)
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" asChild>
                    <Link to="/sites/$siteId" params={{ siteId: savedFlash.siteId }}>View Site</Link>
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setSavedFlash(null)}>Dismiss</Button>
                </div>
              </div>
            </div>
          )}

          {pendingPick && (
            <div className="mt-3 space-y-3 rounded-md border border-border bg-background/60 p-3">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Verify this location on the map</div>
                  <div className="truncate text-sm">{pendingPick.label}</div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {pendingPick.lat.toFixed(5)}, {pendingPick.lng.toFixed(5)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Not quite right? Tap the correct spot on the map to move the pin.
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  value={quickName}
                  onChange={(e) => setQuickName(e.target.value)}
                  placeholder="Site name"
                  className="h-9 sm:max-w-[240px]"
                />
                <div className="flex flex-1 items-center gap-3">
                  <span className="whitespace-nowrap text-xs text-muted-foreground">Perimeter</span>
                  <input
                    type="range"
                    min={25}
                    max={1000}
                    step={25}
                    value={quickRadius}
                    onChange={(e) => setQuickRadius(Number(e.target.value))}
                    className="h-2 w-full flex-1 cursor-pointer accent-[hsl(var(--primary))]"
                    aria-label="Perimeter radius in meters"
                  />
                  <span className="w-16 text-right font-mono text-xs text-muted-foreground">{quickRadius} m</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={quickSave} disabled={quickBusy}>
                  {quickBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & save site"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPendingPick(null)}>Cancel</Button>
                <Button size="sm" variant="ghost" asChild>
                  <Link to="/sites/new">Fine-tune →</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <PatrolMap
          geofences={mapMarkers}
          height={360}
          fitTo="geofences"
          showUserLocation
          enableStyleToggle
          defaultStyle="hybrid"
          focus={focus}
          onMapClick={canEdit ? (lat, lng) => { setPendingPick({ lat, lng, label: `${lat.toFixed(5)}, ${lng.toFixed(5)}` }); setQuickName(""); setSavedFlash(null); } : undefined}
        />

      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sites.map((s) => {
          const isSelected = s.id === selectedId;
          const hasCoords = s.latitude != null && s.longitude != null;
          return (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate({ to: "/sites/$siteId", params: { siteId: s.id } })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate({ to: "/sites/$siteId", params: { siteId: s.id } });
                }
              }}
              className={`group cursor-pointer text-left rounded-xl border bg-card p-5 transition hover:border-primary/40 hover:shadow-md ${
                isSelected ? "border-primary ring-2 ring-primary/30" : "border-border"
              }`}
            >
              <div className="flex items-start justify-between">
                <MapPin className={`h-4 w-4 ${isSelected ? "text-primary" : "text-muted-foreground group-hover:text-primary"}`} />
                <span className={`rounded-full px-2 py-0.5 text-xs ${s.active ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                  {s.active ? "Active" : "Inactive"}
                </span>
              </div>
              <h3 className="mt-3 font-display text-lg font-semibold">{s.name}</h3>
              <p className="text-sm text-muted-foreground">{s.address ?? "No address"}</p>
              {!hasCoords && <p className="mt-2 text-xs text-muted-foreground">No coordinates set</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {hasCoords && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedId(isSelected ? null : s.id);
                    }}
                    className="h-7 px-2 text-xs"
                  >
                    {isSelected ? "Hide on map" : "Show on map"}
                  </Button>
                )}
                {canEdit && (
                  <>
                    <AssignSiteToContractor siteId={s.id} siteName={s.name} />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate({ to: "/sites/$siteId/edit", params: { siteId: s.id } });
                      }}
                      className="h-7 px-2 text-xs"
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSite(s);
                      }}
                      disabled={deletingId === s.id}
                      className="h-7 px-2 text-xs text-destructive border-destructive/40 hover:bg-destructive/10"
                    >
                      <Trash2 className="mr-1 h-3 w-3" />
                      {deletingId === s.id ? "Deleting…" : "Delete"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {sites.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No sites yet. {canEdit && "Click \"Add site\" to create your first one."}
          </div>
        )}
      </div>
    </div>
  );
}
